import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import {
  getRestaurantProfile,
  normalizeKioskStationCount,
} from "../../services/local/restaurantProfile";
import {
  buildKioskLauncherPath,
  isStandaloneAppDisplay,
  loadKioskLauncherConfig,
  loadOrCreateKioskDeviceId,
  normalizeKioskLauncherMode,
  saveKioskLauncherConfig,
} from "../../services/local/kioskLauncher";
import { buildRestaurantBrandTheme } from "../../services/local/brandTheme";
import {
  applyHeadBrandingAssets,
  applyInstitutionalBrandingAssets,
} from "../../services/local/institutionalBranding";
import { isSupabaseConfigured } from "../../services/supabase/config";
import {
  claimKioskTerminalStationInSharedState,
  fetchKioskTerminalClaimsState,
  getActiveKioskTerminalClaims,
  KIOSK_TERMINAL_CLAIM_HEARTBEAT_MS,
  releaseKioskTerminalStationInSharedState,
} from "../../services/supabase/operationalStateService";
import { getPublicKioskMenuByBranchId } from "../../services/supabase/qrService";
import MenuComensalQR from "./MenuComensalQr";

function TotemKiosk() {
  const { branchId = "", stationNumber = "" } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const { availableBranches, currentBranch, setActiveBranch } = useAuth();
  const [publicBranch, setPublicBranch] = useState(null);
  const [loadingPublicBranch, setLoadingPublicBranch] = useState(false);
  const [publicBranchError, setPublicBranchError] = useState("");
  const [stationClaims, setStationClaims] = useState([]);
  const [loadingStationClaims, setLoadingStationClaims] = useState(false);
  const [stationClaimsLoaded, setStationClaimsLoaded] = useState(false);
  const [stationSelectionMessage, setStationSelectionMessage] = useState("");
  const [claimingStationNumber, setClaimingStationNumber] = useState(0);
  const kioskDeviceId = useMemo(() => loadOrCreateKioskDeviceId(), []);
  const launcherMode = useMemo(() => {
    return normalizeKioskLauncherMode(
      new URLSearchParams(location.search).get("launcher")
    );
  }, [location.search]);
  const isAndroidLauncherMode = launcherMode === "android";
  const isWindowsLauncherMode = launcherMode === "windows";
  const isSetupMode = useMemo(
    () => new URLSearchParams(location.search).get("setup") === "1",
    [location.search]
  );
  const isStandaloneDisplayMode = useMemo(() => isStandaloneAppDisplay(), []);
  const kioskDeviceLabel = useMemo(() => {
    if (isAndroidLauncherMode) {
      return "android-launcher";
    }

    if (isWindowsLauncherMode) {
      return "windows-launcher";
    }

    return isStandaloneDisplayMode ? "standalone-kiosk" : "browser-kiosk";
  }, [isAndroidLauncherMode, isStandaloneDisplayMode, isWindowsLauncherMode]);
  const normalizedRouteStationNumber = useMemo(
    () => (stationNumber ? normalizeKioskStationCount(stationNumber) : 0),
    [stationNumber]
  );

  const authenticatedBranch = useMemo(
    () => availableBranches.find((item) => item.id === branchId) || null,
    [availableBranches, branchId]
  );
  const branch = authenticatedBranch || publicBranch || null;

  const branchProfile = useMemo(
    () => (branch ? getRestaurantProfile(branch) : null),
    [branch]
  );
  const brandTheme = useMemo(
    () => buildRestaurantBrandTheme(branchProfile || {}),
    [branchProfile]
  );
  const restaurantFaviconUrl =
    brandTheme.effectiveIconLogoUrl || brandTheme.effectiveLogoCompactUrl || "";
  const kioskStationCount = useMemo(
    () => normalizeKioskStationCount(branchProfile?.kioskStationCount),
    [branchProfile]
  );
  const occupiedStationsByOtherDevice = useMemo(
    () =>
      stationClaims.filter((claim) => claim.deviceId && claim.deviceId !== kioskDeviceId),
    [kioskDeviceId, stationClaims]
  );
  const occupiedStationNumberSet = useMemo(
    () => new Set(occupiedStationsByOtherDevice.map((claim) => claim.stationNumber)),
    [occupiedStationsByOtherDevice]
  );
  const availableStationNumbers = useMemo(
    () =>
      Array.from({ length: kioskStationCount }, (_, index) => index + 1).filter(
        (candidateStationNumber) =>
          !occupiedStationNumberSet.has(candidateStationNumber)
      ),
    [kioskStationCount, occupiedStationNumberSet]
  );

  const refreshStationClaims = useCallback(async () => {
    if (!branchId || !isSupabaseConfigured) {
      setStationClaims([]);
      setStationClaimsLoaded(true);
      return [];
    }

    setLoadingStationClaims(true);

    try {
      const result = await fetchKioskTerminalClaimsState({ id: branchId });
      const nextClaims = Array.isArray(result.claims)
        ? result.claims
        : getActiveKioskTerminalClaims(result.payload);
      setStationClaims(nextClaims);
      return nextClaims;
    } catch (error) {
      console.error("No pudimos revisar los totems ocupados:", error);
      return [];
    } finally {
      setLoadingStationClaims(false);
      setStationClaimsLoaded(true);
    }
  }, [branchId]);

  useEffect(() => {
    if (restaurantFaviconUrl) {
      applyHeadBrandingAssets(restaurantFaviconUrl);
      return;
    }

    applyInstitutionalBrandingAssets();
  }, [restaurantFaviconUrl]);

  useEffect(() => {
    if (authenticatedBranch?.id && currentBranch?.id !== authenticatedBranch.id) {
      setActiveBranch(authenticatedBranch.id);
    }
  }, [authenticatedBranch, currentBranch, setActiveBranch]);

  useEffect(() => {
    if (!branchId || !normalizedRouteStationNumber) {
      return;
    }

    if (!launcherMode && !isStandaloneDisplayMode) {
      return;
    }

    saveKioskLauncherConfig({
      branchId,
      stationNumber: normalizedRouteStationNumber,
      launcherMode: launcherMode || "android",
    });
  }, [branchId, isStandaloneDisplayMode, launcherMode, normalizedRouteStationNumber]);

  useEffect(() => {
    if (authenticatedBranch?.id || !branchId || !isSupabaseConfigured) {
      setLoadingPublicBranch(false);
      setPublicBranchError("");
      setPublicBranch((previous) => (authenticatedBranch?.id ? null : previous));
      return;
    }

    let cancelled = false;

    const loadPublicBranch = async () => {
      setLoadingPublicBranch(true);
      setPublicBranchError("");

      try {
        const publicPayload = await getPublicKioskMenuByBranchId(branchId, 1);

        if (cancelled) {
          return;
        }

        const nextPublicBranch =
          publicPayload?.branch && typeof publicPayload.branch === "object"
            ? publicPayload.branch
            : {
                id: branchId,
                name: "Sucursal kiosk",
              };

        setPublicBranch(nextPublicBranch);
      } catch (error) {
        if (cancelled) {
          return;
        }

        setPublicBranch({
          id: branchId,
          name: "Sucursal kiosk",
        });
        setPublicBranchError(
          error?.message ||
            "No pudimos cargar la configuracion publica de este totem."
        );
      } finally {
        if (!cancelled) {
          setLoadingPublicBranch(false);
        }
      }
    };

    void loadPublicBranch();

    return () => {
      cancelled = true;
    };
  }, [authenticatedBranch?.id, branchId]);

  useEffect(() => {
    if (typeof location.state?.kioskMessage !== "string") {
      return;
    }

    const nextMessage = location.state.kioskMessage.trim();

    if (
      nextMessage
        .toLowerCase()
        .includes("no tienes permisos para sincronizar el estado operativo")
    ) {
      setStationSelectionMessage("");
      return;
    }

    setStationSelectionMessage(nextMessage);
  }, [location.state]);

  useEffect(() => {
    if (normalizedRouteStationNumber || !branchId || !isSetupMode) {
      return undefined;
    }

    void refreshStationClaims();

    if (!isSupabaseConfigured || typeof window === "undefined") {
      return undefined;
    }

    const intervalId = window.setInterval(() => {
      void refreshStationClaims();
    }, KIOSK_TERMINAL_CLAIM_HEARTBEAT_MS);

    return () => {
      window.clearInterval(intervalId);
    };
  }, [branchId, isSetupMode, normalizedRouteStationNumber, refreshStationClaims]);

  useEffect(() => {
    if (
      normalizedRouteStationNumber ||
      !branchId ||
      !stationClaimsLoaded ||
      typeof window === "undefined" ||
      location.state?.skipStationRestore === true
    ) {
      return;
    }

    const launcherConfig = loadKioskLauncherConfig(
      launcherMode || (isStandaloneDisplayMode ? "android" : "")
    );

    if (launcherConfig?.branchId !== branchId || !launcherConfig?.stationNumber) {
      return;
    }

    const rememberedStationNumber = normalizeKioskStationCount(
      launcherConfig.stationNumber
    );
    const stationTakenByOtherDevice = stationClaims.some(
      (claim) =>
        claim.stationNumber === rememberedStationNumber &&
        claim.deviceId !== kioskDeviceId
    );

    if (stationTakenByOtherDevice) {
      return;
    }

    const nextPath = buildKioskLauncherPath({
      branchId,
      stationNumber: rememberedStationNumber,
    });

    if (!nextPath) {
      return;
    }

    navigate(
      launcherMode ? `${nextPath}?launcher=${launcherMode}` : nextPath,
      { replace: true }
    );
  }, [
    branchId,
    isStandaloneDisplayMode,
    kioskDeviceId,
    launcherMode,
    location.state,
    navigate,
    normalizedRouteStationNumber,
    stationClaims,
    stationClaimsLoaded,
  ]);

  useEffect(() => {
    if (
      !normalizedRouteStationNumber ||
      !branchId ||
      !kioskDeviceId ||
      !isSupabaseConfigured
    ) {
      return undefined;
    }

    let cancelled = false;

    const syncStationClaim = async () => {
      try {
        const result = await claimKioskTerminalStationInSharedState(
          { id: branchId },
          {
            stationNumber: normalizedRouteStationNumber,
            deviceId: kioskDeviceId,
            deviceLabel: kioskDeviceLabel,
          }
        );

        if (cancelled) {
          return;
        }

        if (Array.isArray(result.claims)) {
          setStationClaims(result.claims);
        }

        if (result.conflict) {
          navigate(
            launcherMode
              ? `/totem/${branchId}?launcher=${launcherMode}`
              : `/totem/${branchId}`,
            {
              replace: true,
              state: {
                kioskMessage: `El Totem ${normalizedRouteStationNumber} ya estaba en uso en otra terminal. Te mostramos solo los puestos libres.`,
                skipStationRestore: true,
              },
            }
          );
        }
      } catch (error) {
        if (!cancelled) {
          console.error("No pudimos reservar este totem en el estado compartido:", error);
        }
      }
    };

    void syncStationClaim();

    const intervalId = window.setInterval(() => {
      void syncStationClaim();
    }, KIOSK_TERMINAL_CLAIM_HEARTBEAT_MS);

    return () => {
      cancelled = true;
      window.clearInterval(intervalId);
      void releaseKioskTerminalStationInSharedState(
        { id: branchId },
        {
          stationNumber: normalizedRouteStationNumber,
          deviceId: kioskDeviceId,
        }
      );
    };
  }, [
    branchId,
    kioskDeviceId,
    kioskDeviceLabel,
    launcherMode,
    navigate,
    normalizedRouteStationNumber,
  ]);

  const handleOpenStation = async (nextStationNumber) => {
    const normalizedStationNumber = normalizeKioskStationCount(nextStationNumber);

    if (occupiedStationNumberSet.has(normalizedStationNumber)) {
      setStationSelectionMessage(
        `El Totem ${normalizedStationNumber} ya esta en uso. Te dejamos elegir solo entre los puestos disponibles.`
      );
      return;
    }

    const nextPath = buildKioskLauncherPath({
      branchId,
      stationNumber: normalizedStationNumber,
    });

    if (!nextPath) {
      return;
    }

    setStationSelectionMessage("");
    setClaimingStationNumber(normalizedStationNumber);

    const openSelectedStation = () => {
      saveKioskLauncherConfig({
        branchId,
        stationNumber: normalizedStationNumber,
        launcherMode: launcherMode || "android",
      });

      navigate(
        launcherMode ? `${nextPath}?launcher=${launcherMode}` : nextPath,
        { replace: true }
      );
    };

    try {
      if (isSupabaseConfigured && kioskDeviceId) {
        const result = await claimKioskTerminalStationInSharedState(
          { id: branchId },
          {
            stationNumber: normalizedStationNumber,
            deviceId: kioskDeviceId,
            deviceLabel: kioskDeviceLabel,
          }
        );

        if (Array.isArray(result.claims)) {
          setStationClaims(result.claims);
        }

        if (result.conflict) {
          setStationSelectionMessage(
            `El Totem ${normalizedStationNumber} se ocupo desde otra terminal hace un momento. Te mostramos solo los puestos libres.`
          );
          return;
        }
      }

      openSelectedStation();
    } catch (error) {
      console.warn(
        "No pudimos sincronizar la reserva del totem; seguimos con vinculacion local:",
        error
      );
      openSelectedStation();
    } finally {
      setClaimingStationNumber(0);
    }
  };

  if (normalizedRouteStationNumber) {
    return (
      <MenuComensalQR
        experienceMode="kiosk"
        kioskBranchId={branchId}
        kioskStationNumber={normalizedRouteStationNumber}
      />
    );
  }

  if (loadingPublicBranch) {
    return (
      <div style={styles.page}>
        <div style={styles.card}>
          <div style={styles.eyebrow}>MozzoApp Kiosk</div>
          <h1 style={styles.title}>Preparando terminal</h1>
          <p style={styles.subtitle}>
            Estamos cargando la configuracion publica de esta sucursal.
          </p>
        </div>
      </div>
    );
  }

  if (!branchId) {
    return (
      <div style={styles.page}>
        <div style={styles.card}>
          <div style={styles.eyebrow}>MozzoApp Kiosk</div>
          <h1 style={styles.title}>Totem sin sucursal</h1>
          <p style={styles.subtitle}>
            No encontramos la sucursal que tiene que abrir esta terminal.
          </p>
        </div>
      </div>
    );
  }

  if (publicBranchError && !authenticatedBranch?.id) {
    return (
      <div style={styles.page}>
        <div style={styles.card}>
          <div style={styles.eyebrow}>MozzoApp Kiosk</div>
          <h1 style={styles.title}>{branch?.name || "No pudimos abrir el totem"}</h1>
          <p style={styles.subtitle}>{publicBranchError}</p>
        </div>
      </div>
    );
  }

  if (!branch) {
    return (
      <div style={styles.page}>
        <div style={styles.card}>
          <div style={styles.eyebrow}>MozzoApp Kiosk</div>
          <h1 style={styles.title}>Sucursal no disponible</h1>
          <p style={styles.subtitle}>
            Esta terminal todavia no encontro la configuracion de la sucursal.
          </p>
        </div>
      </div>
    );
  }

  if (authenticatedBranch?.id && currentBranch?.id !== authenticatedBranch.id) {
    return null;
  }

  const stationButtons =
    availableStationNumbers.length > 0
      ? availableStationNumbers
      : Array.from({ length: kioskStationCount }, (_, index) => index + 1);

  return (
    <div style={styles.page}>
      <div style={styles.card}>
        <div style={styles.eyebrow}>MozzoApp Kiosk</div>
        <h1 style={styles.title}>
          {branch?.name || (isSetupMode ? "Configuracion de totem" : "Terminal bloqueada")}
        </h1>
        <p style={styles.subtitle}>
          {isSetupMode
            ? "Esta terminal queda lista para vincular el puesto de autoservicio que va a usar esta sucursal."
            : "Esta pantalla no permite elegir totems durante la operacion. Si hace falta vincular o cambiar el puesto, debe hacerlo el personal desde el launcher de configuracion."}
        </p>

        {stationSelectionMessage ? (
          <div style={styles.notice}>{stationSelectionMessage}</div>
        ) : null}

        {!isSetupMode ? (
          <>
            <div style={styles.noticeMuted}>
              Este equipo todavia no tiene un totem asignado o perdio la vinculacion.
              Para evitar que los comensales elijan puestos, el selector queda bloqueado en
              modo operativo.
            </div>
            <div style={styles.helper}>
              Abre nuevamente el launcher de configuracion desde MozzoApp o usa el acceso
              preparado por el personal para volver a vincular esta terminal.
            </div>
          </>
        ) : (
          <>
            {loadingStationClaims ? (
              <div style={styles.helper}>
                Revisando que puestos ya estan ocupados para no duplicar totems.
              </div>
            ) : occupiedStationsByOtherDevice.length > 0 &&
              availableStationNumbers.length > 0 ? (
              <div style={styles.helper}>
                Ya ocultamos los totems que otra terminal tiene en uso. Aqui solo
                aparecen los puestos disponibles.
              </div>
            ) : null}

            <div style={styles.stationGrid}>
              {stationButtons.map((nextStationNumber) => {
                const isOccupied = occupiedStationNumberSet.has(nextStationNumber);
                const isClaiming = claimingStationNumber === nextStationNumber;

                return (
                  <button
                    key={`${branchId}-station-${nextStationNumber}`}
                    type="button"
                    onClick={() => void handleOpenStation(nextStationNumber)}
                    style={{
                      ...styles.stationButton,
                      ...(isOccupied ? styles.stationButtonDisabled : {}),
                    }}
                    disabled={isOccupied || isClaiming}
                  >
                    <span style={styles.stationLabel}>Totem</span>
                    <strong style={styles.stationValue}>{nextStationNumber}</strong>
                    <span style={styles.stationStatus}>
                      {isClaiming
                        ? "Reservando..."
                        : isOccupied
                          ? "En uso"
                          : "Disponible"}
                    </span>
                  </button>
                );
              })}
            </div>

            {availableStationNumbers.length === 0 ? (
              <div style={styles.noticeMuted}>
                En este momento todos los totems de esta sucursal figuran en uso.
                Cuando se libere uno, aparecera aqui automaticamente.
              </div>
            ) : null}

            <div style={styles.helper}>
              {isAndroidLauncherMode
                ? "Abre esta pantalla desde Chrome en Android, elige una vez el totem de esta terminal y luego usa Agregar a pantalla principal. MozzoApp recordara ese puesto y tratara de volver directo ahi."
                : isWindowsLauncherMode
                  ? "Si esta pantalla corre desde el launcher Windows, eliges una vez el totem de esta terminal y despues el equipo intentara volver siempre al mismo puesto."
                  : "Elige que totem queres abrir en esta terminal. Los launchers dedicados recuerdan el puesto despues de la primera vinculacion."}
            </div>
          </>
        )}
      </div>
    </div>
  );
}

export default TotemKiosk;

const styles = {
  page: {
    minHeight: "100vh",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    padding: 24,
    backgroundColor: "#f8fafc",
  },
  card: {
    width: "100%",
    maxWidth: 860,
    display: "grid",
    gap: 20,
    backgroundColor: "#ffffff",
    borderRadius: 24,
    border: "1px solid #e2e8f0",
    boxShadow: "0 24px 70px rgba(15, 23, 42, 0.08)",
    padding: 28,
  },
  eyebrow: {
    color: "#f97316",
    fontSize: 14,
    fontWeight: 800,
    textTransform: "uppercase",
    letterSpacing: "0.08em",
  },
  title: {
    margin: 0,
    color: "#0f172a",
    fontSize: 34,
    lineHeight: 1.05,
  },
  subtitle: {
    margin: 0,
    color: "#475569",
    fontSize: 18,
    lineHeight: 1.5,
  },
  stationGrid: {
    display: "grid",
    gridTemplateColumns: "repeat(auto-fit, minmax(180px, 1fr))",
    gap: 16,
  },
  stationButton: {
    appearance: "none",
    border: "1px solid #fdba74",
    borderRadius: 22,
    backgroundColor: "#fff7ed",
    color: "#9a3412",
    minHeight: 150,
    display: "grid",
    placeItems: "center",
    gap: 4,
    cursor: "pointer",
    padding: 18,
    textAlign: "center",
  },
  stationButtonDisabled: {
    borderColor: "#cbd5e1",
    backgroundColor: "#f8fafc",
    color: "#94a3b8",
    cursor: "not-allowed",
  },
  stationLabel: {
    fontSize: 18,
    fontWeight: 700,
  },
  stationValue: {
    fontSize: 42,
    lineHeight: 1,
  },
  stationStatus: {
    fontSize: 13,
    fontWeight: 700,
    letterSpacing: "0.02em",
  },
  notice: {
    padding: "14px 16px",
    borderRadius: 16,
    border: "1px solid #fdba74",
    backgroundColor: "#fff7ed",
    color: "#9a3412",
    lineHeight: 1.5,
  },
  noticeMuted: {
    padding: "14px 16px",
    borderRadius: 16,
    border: "1px solid #cbd5e1",
    backgroundColor: "#f8fafc",
    color: "#475569",
    lineHeight: 1.5,
  },
  helper: {
    color: "#64748b",
    fontSize: 14,
    lineHeight: 1.5,
  },
};
